import { useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, monthlyPayment } from "@/lib/finance";
import { fmtUSD, fmtPct } from "@/lib/format";
import { METROS, getMetro } from "@/data/metros";
import Seo from "@/components/Seo";
import { breadcrumbJsonLd } from "@/lib/seo";
import EditorBadge from "@/components/EditorBadge";
import { MapPin } from "lucide-react";

// Programmatic SEO: /calculator/mortgage/metro/:slug
// Pre-filled with metro-specific median home price + effective property tax rate.
export default function GeoMetroMortgagePage() {
    const { slug } = useParams();
    const metro = getMetro(slug || "");

    const [homePrice, setHomePrice] = useState(metro?.medianHomePrice ?? 450000);
    const [downPct, setDownPct] = useState(20);
    const [rate, setRate] = useState(6.5);
    const [years, setYears] = useState(30);
    const [propertyTax, setPropertyTax] = useState(metro?.propertyTax ?? 1.1);
    const [insurance, setInsurance] = useState(1500);

    const computed = useMemo(() => {
        const hp = toNum(homePrice);
        const dp = toNum(downPct);
        const r = toNum(rate);
        const y = Math.max(1, toNum(years));
        const downAmt = (hp * dp) / 100;
        const loan = Math.max(0, hp - downAmt);
        const pi = monthlyPayment(loan, r, y);
        const tax = (hp * (toNum(propertyTax) / 100)) / 12;
        const ins = toNum(insurance) / 12;
        const mo = pi + tax + ins;
        return { mo, pi, tax, ins, loan, downAmt, totalPaid: pi * y * 12 };
    }, [homePrice, downPct, rate, years, propertyTax, insurance]);

    if (!metro) return <Navigate to="/calculator/mortgage" replace />;

    const title = `${metro.name} Mortgage Calculator — Local Tax & Median Home Price`;
    const description = `Calculate a ${metro.name}, ${metro.state} mortgage with the metro's ${metro.propertyTax}% effective property tax rate and ${fmtUSD(metro.medianHomePrice, { decimals: 0 })} median home price pre-filled.`;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: title,
            applicationCategory: "FinanceApplication",
            operatingSystem: "Any",
            description,
            areaServed: { "@type": "City", name: metro.name, address: { "@type": "PostalAddress", addressRegion: metro.state, addressCountry: "US" } },
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
        breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Mortgage", path: "/calculator/mortgage" },
            { name: metro.stateName, path: `/calculator/mortgage/${metro.state.toLowerCase()}` },
            { name: metro.name, path: `/calculator/mortgage/metro/${metro.slug}` },
        ]),
    ];

    return (
        <>
            <Seo
                title={`${title} | FinCalc`}
                description={description}
                canonical={`/calculator/mortgage/metro/${metro.slug}`}
                jsonLd={jsonLd}
                ogSlug="mortgage"
            />

            <div className="container-editorial pt-8 pb-2">
                <nav className="flex items-center gap-2 text-xs text-muted-foreground" aria-label="Breadcrumb">
                    <Link to="/" className="hover:text-primary">Home</Link>
                    <span>/</span>
                    <Link to="/calculator/mortgage" className="hover:text-primary">Mortgage</Link>
                    <span>/</span>
                    <Link to={`/calculator/mortgage/${metro.state.toLowerCase()}`} className="hover:text-primary">{metro.stateName}</Link>
                    <span>/</span>
                    <span className="text-primary">{metro.name}</span>
                </nav>
            </div>

            <section className="container-editorial pt-4 pb-6 anim-fade-up">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-accent mb-3">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.5} aria-hidden="true" />
                    {metro.name} · {metro.stateName}
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-primary leading-[1.05] tracking-tight text-balance">
                    {metro.name} Mortgage Calculator
                </h1>
                <p className="mt-4 max-w-2xl text-lg text-foreground/80 leading-relaxed">
                    Shopping for a home in {metro.name}? Pre-filled with the metro&apos;s
                    <strong> {fmtPct(metro.propertyTax, 2)} effective property tax rate</strong> and
                    median home price of <strong>{fmtUSD(metro.medianHomePrice, { decimals: 0 })}</strong>.
                </p>
                <div className="mt-5">
                    <EditorBadge />
                </div>
            </section>

            <CalculatorShell
                slug="mortgage"
                hideHeader
                suppressSeo
                explainer={
                    <>
                        <h2>Buying in {metro.name}: what makes this market different</h2>
                        <p>{metro.blurb}</p>
                        <h3>The three numbers that matter most</h3>
                        <ul>
                            <li><strong>Median home price:</strong> {fmtUSD(metro.medianHomePrice, { decimals: 0 })}. Use this as your anchor — homes priced {'>'}30% above it get appraisal scrutiny.</li>
                            <li><strong>Effective property tax rate:</strong> {fmtPct(metro.propertyTax, 2)}. On the median home that&apos;s {fmtUSD(metro.medianHomePrice * metro.propertyTax / 100, { decimals: 0 })}/year in tax alone.</li>
                            <li><strong>Insurance:</strong> varies widely. Coastal / wildfire / hail-prone metros can run 2–3× the national average. Get a quote before you fall in love with a listing.</li>
                        </ul>
                        <h3>Before you close in {metro.name}</h3>
                        <p>
                            Look up the exact property tax rate on the <strong>{metro.state} county assessor&apos;s
                            website</strong> for the specific parcel. Metro-level averages can hide
                            neighborhood-level differences of 0.5% or more, which translates to
                            hundreds of dollars per month on a typical loan.
                        </p>
                        <p>
                            For a full monthly picture, add PMI (0.5–1.5% of loan/year if your
                            down payment is under 20%), HOA dues if applicable, and a
                            maintenance reserve of roughly 1% of home value per year. The
                            sticker payment is usually 20–30% less than the real monthly cost
                            of ownership.
                        </p>
                    </>
                }
            >
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                    <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                        <Field label={`${metro.name} home price`} prefix="$" value={homePrice} onChange={setHomePrice} dataTestId="calc-input-price" hint={`Metro median: ${fmtUSD(metro.medianHomePrice, { decimals: 0 })}`} />
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Down payment" suffix="%" value={downPct} onChange={setDownPct} dataTestId="calc-input-down" />
                            <Field label="Interest rate" suffix="%" value={rate} onChange={setRate} step={0.125} dataTestId="calc-input-rate" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Loan term (years)" value={years} onChange={setYears} dataTestId="calc-input-years" />
                            <Field label={`${metro.name} property tax`} suffix="%" value={propertyTax} onChange={setPropertyTax} step={0.05} dataTestId="calc-input-tax" hint={`Default: ${fmtPct(metro.propertyTax, 2)} metro avg`} />
                        </div>
                        <Field label="Annual home insurance" prefix="$" value={insurance} onChange={setInsurance} dataTestId="calc-input-ins" />
                        <div className="pt-3 flex flex-wrap gap-4 text-xs text-muted-foreground font-mono tabular">
                            <span>Down: {fmtUSD(computed.downAmt)}</span>
                            <span>Loan: {fmtUSD(computed.loan)}</span>
                        </div>
                    </div>
                    <div className="lg:col-span-2">
                        <ResultStat
                            label={`${metro.name} monthly payment`}
                            value={computed.mo}
                            format={(n) => fmtUSD(n, { decimals: 0 })}
                            secondary={[
                                { label: "P&I", value: computed.pi, format: (n) => fmtUSD(n), testId: "calc-result-pi" },
                                { label: "Tax + Ins", value: computed.tax + computed.ins, format: (n) => fmtUSD(n), testId: "calc-result-escrow" },
                                { label: "Total paid", value: computed.totalPaid, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalpaid" },
                                { label: "Total interest", value: computed.totalPaid - computed.loan, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-totalinterest" },
                            ]}
                        />
                    </div>
                </div>

                <div className="mt-10 bg-card border border-border rounded-xl p-6">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-accent">Other major metros</div>
                    <h3 className="font-serif text-2xl text-primary mt-1 mb-4">Compare with another city</h3>
                    <div className="flex flex-wrap gap-2">
                        {METROS.filter((m) => m.slug !== metro.slug).slice(0, 12).map((m) => (
                            <Link
                                key={m.slug}
                                to={`/calculator/mortgage/metro/${m.slug}`}
                                className="text-xs px-3 py-1.5 rounded-full border border-border hover:border-primary/40 text-primary transition-colors"
                                data-testid={`nearby-metro-${m.slug}`}
                            >
                                {m.name}
                            </Link>
                        ))}
                        <Link
                            to="/calculator/mortgage"
                            className="text-xs px-3 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-secondary transition-colors"
                        >
                            All US →
                        </Link>
                    </div>
                </div>
            </CalculatorShell>
        </>
    );
}
